<template>
  <div class="import-results">
    <!-- Processing State -->
    <div v-if="isProcessing" class="processing-state">
      <h3 class="mb-4">Import in Progress</h3>

      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progress.percentage + '%' }"></div>
        </div>
        <div class="progress-text">
          {{ progress.processed }} of {{ progress.total }} rows processed ({{ progress.percentage }}%)
        </div>
      </div>

      <div class="status-message">
        <div class="spinner"></div>
        <span>Processing your student data...</span>
      </div>

      <p class="text-body-2 mt-4">
        Please wait while we import your students. This may take a few minutes for large files.
        You can safely leave this page - the import will continue in the background.
      </p>
    </div>

    <!-- Failed State -->
    <div v-else-if="isFailed" class="failed-state">
      <h3 class="mb-4">Import Failed</h3>

      <div class="alert inline error">
        <i class="icon error" aria-hidden="true">!</i>
        <span>{{ failureReason || 'An unexpected error occurred during import.' }}</span>
      </div>

      <p class="text-body-2 mt-4">
        Please check your CSV file and try again. If the problem persists, contact support.
      </p>
    </div>

    <!-- Completed State -->
    <div v-else-if="isCompleted">
      <h3 class="mb-4">Import Results</h3>

      <div class="alert inline success">
        Successfully imported {{ importedCount }} students!
      </div>

      <!-- Errors Section -->
      <div class="errors-section" v-if="errorCount > 0">
        <h4 class="errors-heading">Errors ({{ errorCount }})</h4>
        <div class="errors-list-container">
          <ul class="errors-list">
            <li v-for="(error, index) in errors" :key="index" class="error-item">
              <span class="error-icon">!</span>
              <span class="error-message">{{ error }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Outside Service Area Section -->
      <div class="warnings-section" v-if="outsideServiceAreaCount > 0">
        <h4 class="warnings-heading">Outside Service Area ({{ outsideServiceAreaCount }})</h4>
        <div class="warnings-list-container">
          <ul class="warnings-list">
            <li v-for="(student, index) in studentsOutsideServiceArea" :key="index" class="warning-item">
              <span class="warning-icon">!</span>
              <div class="warning-content">
                <div class="warning-title">{{ student }}</div>
                <div class="warning-subtitle">Outside service area</div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div class="mt-6 text-center">
        <p class="text-body-2">
          The import process is complete. You can now access these students in your student directory.
        </p>
      </div>
    </div>

    <!-- Pending State (waiting to start) -->
    <div v-else class="pending-state">
      <h3 class="mb-4">Import Queued</h3>
      <div class="status-message">
        <div class="spinner"></div>
        <span>Waiting for import to start...</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    results: {
      type: Object,
      default: () => ({})
    },
    status: {
      type: String,
      default: null
    },
    progress: {
      type: Object,
      default: () => ({
        total: 0,
        processed: 0,
        percentage: 0
      })
    },
    failureReason: {
      type: String,
      default: null
    }
  },


  computed: {
    isProcessing() {
      return this.status === 'processing' || this.status === 'pending';
    },

    isCompleted() {
      return this.status === 'completed' || (!this.status && this.results && Object.keys(this.results).length > 0);
    },

    isFailed() {
      return this.status === 'failed';
    },

    // Handle both old format (results.errors) and new format (results.errors from status endpoint)
    errors() {
      return this.results?.errors || [];
    },

    errorCount() {
      return this.errors.length;
    },

    studentsOutsideServiceArea() {
      return this.results?.students_outside_service_area || [];
    },

    outsideServiceAreaCount() {
      return this.studentsOutsideServiceArea.length;
    },

    importedCount() {
      return this.results?.imported || this.results?.imported_count || 0;
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/css/partials/variables';

.import-results {
  padding: 1rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mt-4 {
  margin-top: 1rem;
}

.mt-6 {
  margin-top: 1.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.text-center {
  text-align: center;
}

/* Progress Bar Styles */
.progress-container {
  margin: 1.5rem 0;
}

.progress-bar {
  height: 24px;
  background-color: #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: $green;
  border-radius: 12px;
  transition: width 0.3s ease;
}

.progress-text {
  margin-top: 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #666;
}

/* Status Message */
.status-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-top: 1rem;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: $green;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Alerts */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alert.success {
  background-color: rgba($green, 0.1);
  color: $dark-green;
  border: 1px solid $green;
}

.alert.error {
  background-color: rgba($red, 0.1);
  color: $red;
  border: 1px solid $red;
}

/* Errors Section */
.errors-section {
  margin-top: 1.5rem;
  background: #fff;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  overflow: hidden;
}

.errors-heading {
  margin: 0;
  padding: 0.75rem 1rem;
  background-color: #ffebee;
  color: #c62828;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 1px solid #ffcdd2;
}

.errors-list-container {
  max-height: 300px;
  overflow-y: auto;
}

.errors-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.error-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #ffebee;
  background: #fff;
}

.error-item:last-child {
  border-bottom: none;
}

.error-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background-color: #c62828;
  color: #fff;
  font-weight: bold;
  font-size: 14px;
  margin-right: 0.75rem;
  margin-top: 2px;
}

.error-message {
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
}

/* Warnings Section */
.warnings-section {
  margin-top: 1.5rem;
  background: #fff;
  border: 1px solid #ffe0b2;
  border-radius: 4px;
  overflow: hidden;
}

.warnings-heading {
  margin: 0;
  padding: 0.75rem 1rem;
  background-color: #fff3e0;
  color: #e65100;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 1px solid #ffe0b2;
}

.warnings-list-container {
  max-height: 300px;
  overflow-y: auto;
}

.warnings-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.warning-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #fff3e0;
  background: #fff;
}

.warning-item:last-child {
  border-bottom: none;
}

.warning-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background-color: #e65100;
  color: #fff;
  font-weight: bold;
  font-size: 14px;
  margin-right: 0.75rem;
  margin-top: 2px;
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-weight: 500;
  color: #333;
  font-size: 1rem;
}

.warning-subtitle {
  font-size: 0.875rem;
  color: #666;
  margin-top: 2px;
}

.text-body-2 {
  font-size: 0.875rem;
  color: #666;
}
</style>
